import React from 'react';

// ============================================================================
// Operation Listen — shared game components + data
// Rebuilt Feb 2026 against the Agent Build Brief.
// ============================================================================

// ------ Data ------------------------------------------------------------
export const RUNGS = [
  { num: 1, name: 'I Feel _________' },
  { num: 2, name: 'One Off, or?' },
  { num: 3, name: 'Old / New?' },
  { num: 4, name: 'On Purpose?' },
  { num: 5, name: "Who's Fault?" },
  { num: 6, name: 'Proof?' },
  { num: 7, name: 'Ask / Demand?' },
];

// Per-rung answer options used in BEGINNER mode. Coach taps a chip;
// Guide sees the same chip lit up read-only.
export const RUNG_OPTIONS = [
  ['Angry', 'Hurt', 'Afraid'],
  ['One Off', 'Pattern'],
  ['Old', 'New'],
  ['Y', 'N', 'M'],
  ['M', 'Th', 'SE'],
  ['Have', 'Seen', 'Heard'],
  ['Action', 'Choice'],
];

export const SCENARIOS = [
  ["Something happened that left you with a strong feeling you haven't named yet.",
   "You've been carrying this feeling for a while and haven't said it out loud."],
  ["Something happened once that you're really upset about.",
   "Something has happened consistently that you want to address."],
  ["This situation brought up something from a long time ago.",
   "This is entirely new — it just happened and it's fresh."],
  ["You believe what happened was intentional.",
   "You're not sure if it was intentional — but it still hurt."],
  ["You know exactly who's responsible — and it's not you.",
   "You played a part in this and you're willing to admit it."],
  ["You have a specific example — something concrete you can point to.",
   "You're working from a feeling — you don't have receipts, but you know what you know."],
  ["You want to ask for something but you're not sure how.",
   "You're done asking — you're ready to say what needs to happen."],
];

export const THREAT_META = [
  null,
  { label: 'Stable',        color: '#2D6B4F' },
  { label: 'Escalating',    color: '#D4863A' },
  { label: 'High',          color: '#C45A3B' },
  { label: 'Full Conflict', color: '#9B2525' },
];

// ------ Icon primitives -------------------------------------------------
const svg = (child) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{child}</svg>
);
export const IconVoice = () => svg(<><path d="M3 12h2" /><path d="M7 8v8" /><path d="M11 5v14" /><path d="M15 8v8" /><path d="M19 10v4" /><path d="M21 12h1" /></>);
export const IconTime = () => svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>);
export const IconHandshake = () => svg(<><path d="M11 17l2 2 5-5" /><path d="M6 11l4-4 3 3-4 4z" /><path d="M14 8l3-3 4 4-3 3" /><path d="M3 15l3 3" /></>);
export const IconScale = () => svg(<><path d="M12 3v18" /><path d="M6 8h12" /><path d="M6 8l-3 6a3 3 0 006 0z" /><path d="M18 8l3 6a3 3 0 01-6 0z" /></>);
export const IconSend = () => svg(<><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></>);
export const IconRefresh = () => svg(<><path d="M3 12a9 9 0 0115.5-6.3L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 01-15.5 6.3L3 16" /><path d="M3 21v-5h5" /></>);

// ------ Shared derivations --------------------------------------------
// A rung counts toward the paraphrase meter ONLY when the Coach actively
// taps it (i.e. actually heard it in the Guide's reply). A "nudge" is a
// separate prompt to Guide to cover a missing rung — it never marks the
// rung as heard and never fills the meter.
export const countRungsHeard = (state) => {
  const r = state?.rungs || [];
  let c = 0;
  for (let i = 0; i < 7; i++) if (r[i]) c++;
  return c;
};

// Nudge cap depends on mode:
//   beginner → Coach may nudge all 7 rungs
//   advanced → Coach may keep at most 2 active nudges (session-wide budget
//              lifted; count is over currently-active nudges since Coach
//              can dismiss them manually)
export const nudgeMax = (state) => (
  (state?.gameMode || 'beginner') === 'beginner' ? 7 : 2
);
export const countActiveNudges = (state) => {
  const n = state?.nudged || [];
  let c = 0;
  for (let i = 0; i < 7; i++) if (n[i]) c++;
  return c;
};

// ------ Base tokens (inline so the aesthetic is guaranteed) -----------
export const OL = {
  bg: '#141410',
  surface: '#1E1E1A',
  surface2: '#28281F',
  border: '#2E2E2A',
  border2: '#3A3A32',
  clay: '#C45A3B',
  clayDim: '#8A3D28',
  clayGlow: 'rgba(196,90,59,0.18)',
  forest: '#2D3B2D',
  forestLit: '#3D5C3D',
  cream: '#F5F2EB',
  amber: '#D4863A',
  danger: '#9B2525',
  stable: '#2D6B4F',
  text: '#E8E4DC',
  textDim: '#7A7870',
  textMuted: '#4A4840',
};

// ------ Dignity Statements (pulled from Listening Chain module) --------
// Suggested phrases the Coach can share with the Guide during the
// Respect stage. Guide sees a light-touch popup — Coach's pick is
// framed as a suggestion, never a script.
export const DIGNITY_STATEMENTS = [
  {
    category: 'Normalizing',
    phrases: [
      'That happens more than people think.',
      'Plenty of people end up in that spot.',
      'That reaction makes sense given what was going on.',
    ],
  },
  {
    category: 'Reflecting Understanding',
    phrases: [
      'I hear you.',
      'That was hard.',
      'Thanks for telling me that straight.',
      'I get why that happened.',
    ],
  },
  {
    category: 'Removing Shame',
    phrases: [
      'You had a lot going on.',
      'You did what you could with what you had at the time.',
      'Given the circumstances, that tracks.',
    ],
  },
  {
    category: 'Non-Judgmental',
    phrases: [
      "I'm not judging that.",
      'No judgment here.',
      "That's just what happened. Nothing more to make of it.",
    ],
  },
  {
    category: 'Encouraging',
    phrases: [
      "Cool that you didn't hold back with me.",
      'I appreciate you being straight with me about that.',
      'What matters now is what you do next.',
    ],
  },
];

// ------ Section label ---------------------------------------------------
export const SectionLabel = ({ children, right }) => (
  <div className="flex items-center justify-between mb-2">
    <div style={{
      fontSize: '10px', fontWeight: 700, letterSpacing: '0.14em',
      textTransform: 'uppercase', color: OL.textMuted,
    }}>{children}</div>
    {right}
  </div>
);

// ------ Listen-icon toggle (Voice / Time) -------------------------------
export const ListenIconBtn = ({ icon, label, sub, active, onClick, testId, readOnly }) => (
  <button
    onClick={readOnly ? undefined : onClick}
    disabled={readOnly}
    data-testid={testId}
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      padding: '10px 8px 8px', borderRadius: 12,
      background: active ? OL.clayGlow : OL.surface2,
      border: `1.5px solid ${active ? OL.clay : OL.border}`,
      color: OL.text, position: 'relative',
      cursor: readOnly ? 'default' : 'pointer',
      transition: 'all .2s ease',
      opacity: readOnly && !active ? 0.7 : 1,
    }}
  >
    <div style={{
      width: 36, height: 36, borderRadius: '50%',
      background: active ? OL.clay : OL.surface, color: active ? '#fff' : OL.textDim,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: `1px solid ${active ? OL.clay : OL.border2}`,
    }}>{icon}</div>
    <div style={{ fontSize: 12, fontWeight: 600, color: active ? OL.cream : OL.text }}>{label}</div>
    {sub && <div style={{ fontSize: 10, color: OL.textDim }}>{sub}</div>}
    {active && (
      <div style={{
        position: 'absolute', top: 6, right: 6, width: 16, height: 16, borderRadius: '50%',
        background: OL.clay, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, color: '#fff', fontWeight: 700,
      }}>✓</div>
    )}
  </button>
);

// ------ Rung chip (Coach — interactive) ---------------------------------
// `tapped`  = Coach heard this rung in Guide's reply (fills the chip clay,
//             counts toward the Paraphrase Meter).
// `nudged`  = Coach is prompting Guide to cover a MISSING rung. Visual is a
//             separate pulsing outline + active send icon — it never fills
//             the chip and never counts toward the meter.
export const RungChip = ({
  num, name, tapped, nudged, canNudge, nudgeDisabled, onTap, onNudge,
  testId, testIdNudge,
  // Beginner-mode props
  beginnerOptions, beginnerValue, onBeginnerSelect, testIdBeginner,
}) => {
  // Any click inside the chip that isn't on the Nudge icon or a Beginner
  // option counts as "tap this rung". Nudge and Beginner controls stop
  // propagation so they retain their own dedicated handlers.
  const stop = (e) => e.stopPropagation();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onTap}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap && onTap(); }
      }}
      aria-pressed={tapped ? 'true' : 'false'}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        minHeight: 44,
        padding: '6px 10px', borderRadius: 10,
        background: tapped ? OL.clayGlow : OL.surface2,
        border: `1.5px solid ${tapped ? OL.clay : (nudged ? OL.amber : OL.border)}`,
        boxShadow: (nudged && !tapped) ? `0 0 10px rgba(212,134,58,0.35)` : 'none',
        transition: 'all .18s ease',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
      }}
      data-testid={testId}
      data-nudged={nudged ? 'true' : 'false'}
      data-tapped={tapped ? 'true' : 'false'}
    >
      <div
        aria-hidden
        style={{
          width: 26, height: 26, borderRadius: '50%',
          background: tapped ? OL.clay : OL.surface,
          color: tapped ? '#fff' : OL.textDim,
          border: `1px solid ${tapped ? OL.clay : (nudged ? OL.amber : OL.border2)}`,
          fontSize: 10, fontWeight: 700, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >{num}</div>
      <div
        style={{
          textAlign: 'left',
          color: tapped ? OL.cream : OL.text, fontSize: 12,
          padding: '2px 0', whiteSpace: 'nowrap', flexShrink: 0,
        }}
      >{name}</div>
      {beginnerOptions && (
        <div
          data-testid={testIdBeginner}
          onClick={stop}
          style={{ display: 'inline-flex', gap: 3, flex: 1, justifyContent: 'flex-end' }}
        >
          {beginnerOptions.map((opt) => {
            const active = beginnerValue === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={(e) => { stop(e); onBeginnerSelect && onBeginnerSelect(opt); }}
                data-testid={`${testIdBeginner}-opt-${opt}`}
                data-active={active ? 'true' : 'false'}
                style={{
                  minWidth: 34, height: 28, borderRadius: 999,
                  padding: '0 10px',
                  background: active ? OL.clay : 'transparent',
                  border: `1px solid ${active ? OL.clay : OL.border2}`,
                  color: active ? '#fff' : OL.textDim,
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                  cursor: onBeginnerSelect ? 'pointer' : 'default',
                  touchAction: 'manipulation',
                }}
              >{opt}</button>
            );
          })}
        </div>
      )}
      {!beginnerOptions && <div style={{ flex: 1 }} />}
      {canNudge && (
        <button
          type="button"
          onClick={(e) => { stop(e); onNudge && onNudge(); }}
          disabled={nudgeDisabled && !nudged}
          title={nudged ? 'Nudge active — tap to dismiss' : 'Nudge Guide on this rung'}
          data-testid={testIdNudge}
          style={{
            width: 28, height: 28, borderRadius: '50%',
            background: nudged ? OL.amber : 'transparent',
            border: `1px solid ${nudged ? OL.amber : OL.border2}`,
            color: nudged ? '#fff' : OL.textDim,
            opacity: (nudgeDisabled && !nudged) ? 0.2 : (nudged ? 1 : 0.55),
            cursor: (nudgeDisabled && !nudged) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all .15s ease', flexShrink: 0,
            touchAction: 'manipulation',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" />
          </svg>
        </button>
      )}
    </div>
  );
};

// ------ Guide rung chip (read-only mirror) ------------------------------
// `tapped` = Coach heard this rung (fills chip clay on the Guide side too).
// `nudged` = Coach is prompting Guide to cover THIS rung. Renders as a
//            distinct amber pulsing outline + arrow badge, without filling
//            the chip. Guide can still act; a nudge is a signal, not a mark.
export const GuideRungChip = ({ num, name, tapped, nudged, testId, beginnerOptions, beginnerValue }) => (
  <div
    data-testid={testId}
    data-nudged={nudged ? 'true' : 'false'}
    data-tapped={tapped ? 'true' : 'false'}
    style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '5px 9px', borderRadius: 9,
      background: tapped ? OL.clayGlow : OL.surface2,
      border: `1.5px solid ${tapped ? OL.clay : (nudged ? OL.amber : OL.border)}`,
      pointerEvents: 'none',
      boxShadow: (nudged && !tapped) ? `0 0 12px rgba(212,134,58,0.45)` : 'none',
      animation: (nudged && !tapped) ? 'olNudgePulse 1.6s ease-in-out infinite' : 'none',
    }}
  >
    <div style={{
      width: 24, height: 24, borderRadius: '50%',
      background: tapped ? OL.clay : OL.surface,
      color: tapped ? '#fff' : OL.textDim,
      border: `1px solid ${tapped ? OL.clay : (nudged ? OL.amber : OL.border2)}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 10, fontWeight: 700, flexShrink: 0,
    }}>{num}</div>
    <div style={{
      color: tapped ? OL.cream : OL.text, fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0,
    }}>{name}</div>
    {beginnerOptions && (
      <div style={{ display: 'inline-flex', gap: 3, flex: 1, justifyContent: 'flex-end' }}>
        {beginnerOptions.map((opt) => {
          const active = beginnerValue === opt;
          return (
            <span
              key={opt}
              style={{
                minWidth: 28, height: 20, borderRadius: 999,
                padding: '0 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: active ? OL.clay : 'transparent',
                border: `1px solid ${active ? OL.clay : OL.border2}`,
                color: active ? '#fff' : OL.textDim,
                fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
              }}
            >{opt}</span>
          );
        })}
      </div>
    )}
    {!beginnerOptions && <div style={{ flex: 1 }} />}
    {nudged && (
      <span style={{
        fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: '#fff', background: OL.amber,
        padding: '2px 6px', borderRadius: 4, flexShrink: 0,
      }}>NUDGE</span>
    )}
  </div>
);

// ------ Nudge dots ------------------------------------------------------
// Renders `max` dots (default 2 for Advanced; 7 for Beginner) with `used`
// filled in the clay accent. Beginner mode gets a compact rail.
export const NudgeDots = ({ used, max = 2 }) => (
  <div style={{ display: 'inline-flex', gap: max > 3 ? 3 : 5, alignItems: 'center' }}>
    {Array.from({ length: max }).map((_, i) => (
      <span key={i}
        style={{
          width: max > 3 ? 6 : 8, height: max > 3 ? 6 : 8, borderRadius: '50%',
          background: i < used ? OL.amber : 'transparent',
          border: `1.5px solid ${i < used ? OL.amber : OL.border2}`,
          boxShadow: i < used ? `0 0 6px rgba(212,134,58,0.35)` : 'none',
        }} />
    ))}
  </div>
);

// ------ Paraphrase meter ------------------------------------------------
export const ParaphraseMeter = ({ count, readOnly }) => {
  const pct = Math.round((count / 7) * 100);
  return (
    <div style={{ opacity: readOnly ? 0.85 : 1 }} data-testid="ol-para-meter">
      <SectionLabel>Paraphrase Meter {readOnly && '· read-only'}</SectionLabel>
      <div style={{
        width: '100%', height: 6, borderRadius: 3, background: OL.surface2,
        border: `1px solid ${OL.border}`, overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', width: `${pct}%`, background: OL.clay,
          transition: 'width .4s cubic-bezier(.4,0,.2,1)',
          boxShadow: `0 0 8px ${OL.clayGlow}`,
        }} />
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', marginTop: 4,
        fontSize: 10, color: OL.textDim,
      }}>
        <span>{count} / 7 rungs</span>
        <span>{pct}%</span>
      </div>
    </div>
  );
};

// ------ Action icon button (Handshake / Scale) — vertical format matching ListenIconBtn
export const ActionIconBtn = ({ icon, label, sub, active, onClick, testId, readOnly }) => (
  <button
    onClick={readOnly ? undefined : onClick}
    data-testid={testId}
    disabled={readOnly}
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      padding: '10px 8px 8px', borderRadius: 12,
      background: active ? OL.clayGlow : OL.surface2,
      border: `1.5px solid ${active ? OL.clay : OL.border}`,
      color: OL.text, position: 'relative', flex: 1,
      cursor: readOnly ? 'default' : 'pointer',
      transition: 'all .18s ease',
      opacity: readOnly && !active ? 0.7 : 1,
    }}
  >
    {active && (
      <div style={{
        position: 'absolute', top: 6, right: 6,
        width: 14, height: 14, borderRadius: '50%', background: OL.clay,
        color: '#fff', fontSize: 9, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>✓</div>
    )}
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: active ? OL.clay : OL.surface, color: active ? '#fff' : OL.textDim,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: `1px solid ${active ? OL.clay : OL.border2}`,
    }}>{icon}</div>
    <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.1 }}>{label}</div>
    {sub && <div style={{ fontSize: 9.5, color: OL.textMuted, lineHeight: 1.1, textAlign: 'center' }}>{sub}</div>}
  </button>
);

// ------ Threat meter ----------------------------------------------------
export const ThreatMeter = ({ level, onChange, editable }) => {
  const meta = THREAT_META[level] || THREAT_META[1];
  const bump = (dir) => onChange(Math.max(1, Math.min(4, level + dir)));
  return (
    <div>
      <SectionLabel right={
        <span style={{ fontSize: 11, fontWeight: 600, color: meta.color }}>{meta.label}</span>
      }>Threat Meter</SectionLabel>
      <div style={{ display: 'flex', gap: 4 }} data-testid="ol-threat-pips">
        {[1, 2, 3, 4].map((i) => {
          const lit = i <= level;
          const c = THREAT_META[i].color;
          return (
            <div key={i}
              style={{
                flex: 1, height: 10, borderRadius: 3,
                background: lit ? c : OL.surface,
                border: `1px solid ${lit ? c : OL.border}`,
                boxShadow: lit ? `0 0 ${i >= 4 ? '10px' : '6px'} ${c}66` : 'none',
                transition: 'all .2s ease',
              }}
              data-testid={`ol-threat-pip-${i}`}
            />
          );
        })}
      </div>
      {editable && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button
            onClick={() => bump(1)}
            disabled={level >= 4}
            data-testid="ol-threat-up"
            style={{
              flex: 1, padding: '9px 8px', borderRadius: 999,
              background: 'rgba(196,90,59,0.14)', color: OL.clay,
              border: `1px solid ${OL.clay}40`, fontSize: 11, fontWeight: 600,
              cursor: level >= 4 ? 'not-allowed' : 'pointer', opacity: level >= 4 ? 0.3 : 1,
            }}>Escalate ↑</button>
          <button
            onClick={() => bump(-1)}
            disabled={level <= 1}
            data-testid="ol-threat-down"
            style={{
              flex: 1, padding: '9px 8px', borderRadius: 999,
              background: OL.surface2, color: OL.text,
              border: `1px solid ${OL.border2}`, fontSize: 11, fontWeight: 600,
              cursor: level <= 1 ? 'not-allowed' : 'pointer', opacity: level <= 1 ? 0.3 : 1,
            }}>De-escalate ↓</button>
        </div>
      )}
    </div>
  );
};

// ------ Conflict card (drawn rung + 2 scenarios + redraw) --------------
export const ConflictCard = ({ drawnRung, selectedScenario, onSelectScenario, onRedraw }) => {
  const rung = RUNGS[drawnRung] || RUNGS[0];
  const scenarios = SCENARIOS[drawnRung] || SCENARIOS[0];
  return (
    <div data-testid="ol-conflict-card">
      <SectionLabel right={
        <button onClick={onRedraw} data-testid="ol-conflict-redraw"
          style={{
            fontSize: 10, color: OL.textDim, background: 'transparent',
            border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
          }}><IconRefresh /> Redraw</button>
      }>Drawn Rung</SectionLabel>
      <div style={{
        background: OL.surface2, border: `1.5px solid ${OL.border2}`,
        borderRadius: 14, padding: '18px 16px', marginBottom: 12,
      }}>
        <div style={{
          fontFamily: '"DM Serif Display", Georgia, serif',
          fontSize: 24, color: OL.cream, lineHeight: 1.1, marginBottom: 6,
        }}>{rung.name}</div>
        <div style={{ fontSize: 10, color: OL.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          Rung {rung.num} of 7
        </div>
      </div>

      <SectionLabel>Your scenario</SectionLabel>
      <div
        data-testid="ol-conflict-scenario"
        style={{
          background: OL.clayGlow,
          border: `1.5px solid ${OL.clay}`,
          borderRadius: 12,
          padding: '14px 16px',
          color: OL.cream,
          fontSize: 13, lineHeight: 1.45,
        }}
      >
        {scenarios[selectedScenario ?? 0]}
      </div>
    </div>
  );
};

// ------ Stance buttons (Own vs Blame) -----------------------------------
export const StanceButtons = ({ stance, onSet }) => (
  <div>
    <SectionLabel>Your Stance</SectionLabel>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button
        onClick={() => onSet('own')}
        data-testid="ol-stance-own"
        style={{
          padding: '14px 12px', borderRadius: 12, textAlign: 'left', cursor: 'pointer',
          background: stance === 'own' ? 'rgba(45,107,79,0.20)' : OL.surface2,
          border: `1.5px solid ${stance === 'own' ? OL.forestLit : OL.border}`,
          color: stance === 'own' ? OL.cream : OL.text,
          boxShadow: stance === 'own' ? `0 0 14px rgba(45,107,79,0.35)` : 'none',
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: stance === 'own' ? OL.forestLit : OL.textMuted, marginBottom: 4 }}>Own</div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>“I feel _________”</div>
      </button>
      <button
        onClick={() => onSet('blame')}
        data-testid="ol-stance-blame"
        style={{
          padding: '14px 12px', borderRadius: 12, textAlign: 'left', cursor: 'pointer',
          background: stance === 'blame' ? OL.clayGlow : OL.surface2,
          border: `1.5px solid ${stance === 'blame' ? OL.clay : OL.border}`,
          color: stance === 'blame' ? OL.cream : OL.text,
          boxShadow: stance === 'blame' ? `0 0 14px ${OL.clayGlow}` : 'none',
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: stance === 'blame' ? OL.clay : OL.textMuted, marginBottom: 4 }}>Blame</div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>“You / They make me _________”</div>
      </button>
    </div>
  </div>
);

// ------ Panel shell -----------------------------------------------------
export const Panel = ({ title, right, children, testId }) => (
  <div
    data-testid={testId}
    style={{
      background: OL.surface, border: `1px solid ${OL.border}`, borderRadius: 14,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      minHeight: 0, minWidth: 0, flex: 1, width: '100%', position: 'relative',
    }}
  >
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 14px', borderBottom: `1px solid ${OL.border}`,
      background: OL.surface, position: 'sticky', top: 0, zIndex: 1,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: OL.cream,
      }}>{title}</div>
      {right}
    </div>
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: 1 }}>
      {children}
    </div>
  </div>
);

export const Divider = () => (
  <div style={{ height: 1, background: OL.border, margin: '4px 0' }} />
);

// ------ Full role views (composed) --------------------------------------

// Reset payload used by End Game — clears round-scoped fields.
export const ROUND_RESET = {
  rungs: Array(7).fill(false),
  nudged: Array(7).fill(false),
  nudgesUsed: 0,
  coachVoice: false,
  coachTime: false,
  coachHandshake: false,
  coachScale: false,
  paraphraseSubmitted: false,
  respectComplete: false,
  nextStepsComplete: false,
  // Conflict-side + shared round fields cleared so the demo starts truly fresh.
  conflictEngaged: null,
  dignitySuggestion: null,
  story: '',
  challenge: '',
  rungWhere: 'beginning',
  rungAnswers: {},
  nextAction: '',
  drawnRung: 0,
  selectedScenario: null,
};

const EndGamePanel = ({ testId, canReset, onReset }) => (
  <div data-testid={testId} style={{
    background: `linear-gradient(135deg, ${OL.forest}66 0%, ${OL.forestLit}33 100%)`,
    border: `1.5px solid ${OL.forestLit}`,
    borderRadius: 14, padding: '18px 18px', textAlign: 'center',
  }}>
    <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: OL.forestLit, fontWeight: 700, marginBottom: 6 }}>
      Round complete
    </div>
    <div style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 26, color: OL.cream, marginBottom: 12 }}>
      Well played.
    </div>
    {canReset ? (
      <button onClick={onReset} data-testid={`${testId}-reset`}
        style={{
          background: OL.clay, border: 0, borderRadius: 999,
          padding: '10px 22px', color: '#fff', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', letterSpacing: '0.04em',
        }}>
        End Game · Start a new round
      </button>
    ) : (
      <div style={{ fontSize: 12, color: OL.textDim, fontStyle: 'italic' }}>
        Waiting for the Coach to close it out.
      </div>
    )}
  </div>
);

const stageFlags = (state) => ({
  voiceTimeDone: !!state?.coachVoice && !!state?.coachTime,
  paraphrased: !!state?.paraphraseSubmitted,
  respectComplete: !!state?.respectComplete,
  nextStepsComplete: !!state?.nextStepsComplete,
  closingDone: !!state?.respectComplete && !!state?.nextStepsComplete,
});

// Ultra-compact labeled toggle pair — for Story (O/S) and Challenge (A1/A2).
// Coach uses interactive; Guide uses readOnly.
const MicroToggle = ({ label, value, options, onSelect, readOnly, testId }) => (
  <div
    data-testid={testId}
    style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: OL.surface2, border: `1px solid ${OL.border2}`,
      borderRadius: 999, padding: '4px 6px 4px 10px',
    }}
  >
    <span style={{ fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: OL.textMuted, fontWeight: 600 }}>
      {label}
    </span>
    <div style={{ display: 'inline-flex', gap: 2 }}>
      {options.map((opt) => {
        const optValue = typeof opt === 'string' ? opt : opt.value;
        const optLabel = typeof opt === 'string' ? opt : opt.label;
        const active = value === optValue;
        return (
          <button
            key={optValue}
            onClick={readOnly ? undefined : () => onSelect(optValue)}
            disabled={!!readOnly}
            data-testid={`${testId}-opt-${optValue}`}
            data-active={active ? 'true' : 'false'}
            style={{
              minWidth: 26, height: 22, borderRadius: 999, border: 0,
              padding: '0 8px',
              background: active ? OL.clay : 'transparent',
              color: active ? '#fff' : OL.textDim,
              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
              cursor: readOnly ? 'default' : 'pointer',
            }}
          >{optLabel}</button>
        );
      })}
    </div>
  </div>
);

// Next Steps sub-toggles — three yes/no reads surfaced after the Coach taps
// "Next Steps". Stored on state as nsAction / nsDecision / nsCommitment.
const NEXT_STEPS_QS = [
  { key: 'nsAction',     label: 'Action' },
  { key: 'nsDecision',   label: 'Decision' },
  { key: 'nsCommitment', label: 'Commitment' },
];

const NextStepsSubToggles = ({ state, write, readOnly, testPrefix }) => (
  <div
    data-testid={`${testPrefix}-ns-subtoggles`}
    style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}
  >
    {NEXT_STEPS_QS.map((q) => {
      const value = state?.[q.key] === 'yes' ? 'yes'
                   : state?.[q.key] === 'no' ? 'no' : null;
      return (
        <div
          key={q.key}
          data-testid={`${testPrefix}-ns-${q.key}`}
          style={{
            display: 'flex', alignItems: 'center',
            padding: '7px 10px', borderRadius: 10,
            background: OL.surface2, border: `1px solid ${OL.border2}`,
          }}
        >
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: OL.textDim, flex: 1,
          }}>{q.label}</div>
          <div style={{ display: 'inline-flex', gap: 4 }}>
            {['yes', 'no'].map((v) => {
              const active = value === v;
              return (
                <button
                  key={v}
                  onClick={readOnly ? undefined : () => {
                    if (!write) return;
                    write({ [q.key]: active ? null : v });
                  }}
                  disabled={readOnly}
                  data-testid={`${testPrefix}-ns-${q.key}-${v}`}
                  data-active={active ? 'true' : 'false'}
                  style={{
                    minWidth: 46, padding: '6px 12px', borderRadius: 999,
                    background: active ? OL.clay : 'transparent',
                    border: `1px solid ${active ? OL.clay : OL.border2}`,
                    color: active ? '#fff' : OL.textDim,
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    cursor: readOnly ? 'default' : 'pointer',
                    touchAction: 'manipulation',
                  }}
                >{v}</button>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);

const MicroToggleRow = ({ state, write, readOnly, testPrefix }) => (
  <div
    data-testid={`${testPrefix}-micro-toggles`}
    style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}
  >
    {/* Where — moved into the former Focus slot. */}
    <MicroToggle
      label="Where"
      value={state?.rungWhere === 'middle' ? 'middle' : state?.rungWhere === 'end' ? 'end' : 'beginning'}
      options={[
        { value: 'beginning', label: 'Beginning' },
        { value: 'middle', label: 'Middle' },
        { value: 'end', label: 'End' },
      ]}
      readOnly={readOnly}
      onSelect={(v) => write && write({ rungWhere: v })}
      testId={`${testPrefix}-toggle-where`}
    />
    {/* Challenge — promoted from the Next Steps stage into the second slot
        so it's visible from the start of the round. */}
    <MicroToggle
      label="Challenge"
      value={state?.nextAction === 'choice' ? 'choice' : 'action'}
      options={[
        { value: 'action', label: 'Action' },
        { value: 'choice', label: 'Choice' },
      ]}
      readOnly={readOnly}
      onSelect={(v) => write && write({ nextAction: v })}
      testId={`${testPrefix}-toggle-challenge`}
    />
  </div>
);

// Legacy no-op kept so any remaining references stay harmless. The Challenge
// toggle lives in MicroToggleRow now (always visible from round start).
const NextActionToggle = () => null;

// BlurGate — wraps a round section and dims + locks it until its unlock
// condition is met. Used for the three-stage reveal: Voice+Time first, then
// Where/Challenge/Ladder, then Respect/Next-Steps.
const BlurGate = ({ unlocked, children, testId }) => (
  <div
    data-testid={testId}
    data-unlocked={unlocked ? 'true' : 'false'}
    aria-disabled={!unlocked}
    style={{
      position: 'relative',
      filter: unlocked ? 'none' : 'blur(3px) saturate(0.6)',
      opacity: unlocked ? 1 : 0.55,
      pointerEvents: unlocked ? 'auto' : 'none',
      userSelect: unlocked ? 'auto' : 'none',
      transition: 'filter .25s ease, opacity .25s ease',
    }}
  >
    {children}
  </div>
);

export const CoachPanel = ({ state, write, testPrefix = 'ol', panelTitle = 'Coach', voiceCapture = null }) => {
  const count = countRungsHeard(state);
  const activeNudges = countActiveNudges(state);
  const maxNudges = nudgeMax(state);
  const guideView = state.guideView === 'meter' ? 'meter' : 'ladder';
  const nudgesEnabled = guideView === 'meter';
  const { voiceTimeDone, paraphrased, closingDone } = stageFlags(state);
  const ready = count >= 7;
  const stage = stageFromState(state);
  const stageDone = isStageDone(state, stage);
  // Round is over once all three tasks have been marked complete.
  const allDone = !!state.paraphraseSubmitted && !!state.respectComplete && !!state.nextStepsComplete;
  const showBanner = !allDone && stage !== null;

  // Tap = "I heard this rung." Fills the chip clay and counts toward the
  // Paraphrase Meter. Completely independent from nudge.
  const tapRung = (i) => {
    const next = [...(state.rungs || Array(7).fill(false))];
    next[i] = !next[i];
    write({ rungs: next });
  };
  // Nudge = "Guide, please cover this rung." Signal only — never marks the
  // rung as heard and never counts toward the meter. Coach can tap the
  // nudge icon again to dismiss the nudge. Advanced mode caps active
  // nudges at 2; Beginner mode allows nudging all 7.
  const sendNudge = (i) => {
    if (!nudgesEnabled) return;
    const nudged = [...(state.nudged || Array(7).fill(false))];
    const wasNudged = !!nudged[i];
    if (!wasNudged && activeNudges >= maxNudges) return;
    nudged[i] = !wasNudged;
    write({ nudged });
  };
  // Marking a task complete only flags THAT task done — it does not change the
  // toggle state (Respect / Next Steps stay on until Coach turns them off).
  const markStage = () => {
    if (stage === 'paraphrase' && ready) write({ paraphraseSubmitted: true });
    else if (stage === 'dignity') write({ respectComplete: true });
    else if (stage === 'next') write({ nextStepsComplete: true });
  };
  const endGame = () => write(ROUND_RESET);

  return (
    <Panel testId={`${testPrefix}-coach`} title={panelTitle} right={
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {voiceCapture}
        <span style={{ fontSize: 10, color: OL.textDim }}>role</span>
      </div>
    }>
      {/* Instruction panel — appears only when Coach toggles Respect / Next Steps
          (or when meter hits 7/7 for paraphrase). Marking a task complete does NOT
          advance the phase — the banner stays showing the currently-active toggle. */}
      {showBanner && (
        <ParaphrasePrompt
          testId={`${testPrefix}-coach-cta`}
          stage={stage}
          ready={ready}
          done={stageDone}
          onMarkComplete={markStage}
        />
      )}

      {/* Voice + Time — collapses once both are marked */}
      {!voiceTimeDone && (
        <div data-testid={`${testPrefix}-coach-voicetime`}>
          <SectionLabel>Listening Chain</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <ListenIconBtn testId={`${testPrefix}-coach-voice`} icon={<IconVoice />} label="Voice"
              active={!!state.coachVoice} onClick={() => write({ coachVoice: !state.coachVoice })} />
            <ListenIconBtn testId={`${testPrefix}-coach-time`} icon={<IconTime />} label="Time"
              active={!!state.coachTime} onClick={() => write({ coachTime: !state.coachTime })} />
          </div>
        </div>
      )}

      {/* Story · Challenge micro-toggles — Coach picks, Guide sees.
          Positioned beneath Voice + Time. Blurred until Voice + Time are
          both marked so the round opens on a single clear focal point.
          Collapses off-screen once the Paraphrase is marked complete. */}
      {!paraphrased && (
        <BlurGate unlocked={voiceTimeDone} testId={`${testPrefix}-coach-toggles-gate`}>
          <MicroToggleRow state={state} write={write} testPrefix={`${testPrefix}-coach`} />
        </BlurGate>
      )}

      {/* Empathy Ladder — collapses once Coach marks Paraphrase Complete.
          Also gated on Voice + Time so the round progresses in order. */}
      {!paraphrased && (
        <BlurGate unlocked={voiceTimeDone} testId={`${testPrefix}-coach-ladder-gate`}>
          {!voiceTimeDone && <Divider />}
          <div data-testid={`${testPrefix}-coach-ladder-section`}>
            <SectionLabel right={<NudgeDots used={activeNudges} max={maxNudges} />}>Empathy Ladder</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {RUNGS.map((r, i) => {
                const isBeginner = (state.gameMode || 'beginner') === 'beginner';
                const rungAns = (state.rungAnswers || {})[String(i)];
                return (
                  <RungChip
                    key={r.num}
                    testId={`${testPrefix}-coach-rung-${i}`}
                    testIdNudge={`${testPrefix}-coach-nudge-${i}`}
                    num={r.num}
                    name={r.name}
                    tapped={!!(state.rungs || [])[i]}
                    nudged={!!(state.nudged || [])[i]}
                    canNudge={nudgesEnabled}
                    nudgeDisabled={!nudgesEnabled || (activeNudges >= maxNudges && !((state.nudged || [])[i]))}
                    onTap={() => tapRung(i)}
                    onNudge={() => sendNudge(i)}
                    beginnerOptions={isBeginner ? RUNG_OPTIONS[i] : undefined}
                    beginnerValue={rungAns}
                    onBeginnerSelect={isBeginner ? ((opt) => {
                      const answers = { ...(state.rungAnswers || {}) };
                      const nextVal = answers[String(i)] === opt ? '' : opt;
                      answers[String(i)] = nextVal;
                      // Selecting a chip counts as "hearing" that rung — auto-lights
                      // the number circle so the Paraphrase Meter picks it up.
                      // Clearing the chip un-taps the rung.
                      const rungs = (state.rungs || []).slice();
                      while (rungs.length < 7) rungs.push(false);
                      rungs[i] = !!nextVal;
                      write({ rungAnswers: answers, rungs });
                    }) : undefined}
                    testIdBeginner={`${testPrefix}-coach-rung-${i}-answer`}
                  />
                );
              })}
            </div>
            {!nudgesEnabled && (
              <div
                data-testid={`${testPrefix}-coach-nudge-hint`}
                style={{ marginTop: 8, fontSize: 10, color: OL.textMuted, textAlign: 'center', fontStyle: 'italic' }}
              >
                Guide is on the Ladder — taps already light up their rungs. Nudges unlock when Guide switches to Meter.
              </div>
            )}
          </div>
          <Divider />
          <ParaphraseMeter count={count} />
        </BlurGate>
      )}

      {/* Dignity Statement dropdown — active while Respect stage is live.
          Coach picks a suggested phrase; Guide sees it as a suggestion popup. */}
      {paraphrased && state.coachHandshake && !state.respectComplete && (
        <DignityDropdown
          testId={`${testPrefix}-coach-dignity`}
          value={state.dignitySuggestion}
          onSelect={(entry) => write && write({ dignitySuggestion: entry })}
          onClear={() => write && write({ dignitySuggestion: null })}
        />
      )}

      {/* Respect + Next Steps — rendered from the start so both are on screen,
          but blurred until the Paraphrase is marked complete. Respect
          collapses off-screen once marked, leaving only Next Steps. */}
      {!closingDone && (
        <BlurGate unlocked={paraphrased} testId={`${testPrefix}-coach-closing-gate`}>
          {!paraphrased ? null : (voiceTimeDone ? null : <Divider />)}
          <div data-testid={`${testPrefix}-coach-closing-section`} style={{ marginTop: paraphrased ? 0 : 4 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {!state.respectComplete && (
                <ActionIconBtn testId={`${testPrefix}-coach-handshake`} icon={<IconHandshake />} label="Respect"
                  active={!!state.coachHandshake} onClick={() => write({ coachHandshake: !state.coachHandshake })} />
              )}
              <ActionIconBtn testId={`${testPrefix}-coach-scale`} icon={<IconScale />} label="Next Steps"
                active={!!state.coachScale} onClick={() => write({ coachScale: !state.coachScale })} />
            </div>
            {/* Next-Steps sub-toggles — appear once Next Steps is active. */}
            {!!state.coachScale && !state.nextStepsComplete && (
              <NextStepsSubToggles state={state} write={write} testPrefix={`${testPrefix}-coach`} />
            )}
          </div>
        </BlurGate>
      )}

      {/* End Game — appears when all three closing phases are complete */}
      {allDone && (
        <EndGamePanel testId={`${testPrefix}-coach-end-game`} canReset onReset={endGame} />
      )}
    </Panel>
  );
};

// Guide's ladder/meter view is chosen BEFORE the game starts (on the demo-room
// landing screen). Once inside the round it is a fixed read-only choice — no
// mid-round toggle is exposed on either Coach or Guide.

// Coach-side dropdown: pick a Dignity Statement to suggest to the Guide.
// The choice is persisted on room state as `dignitySuggestion` = {category, phrase}.
// Guide sees a lightweight popup — see `DignitySuggestionPopup`.
const DignityDropdown = ({ testId, value, onSelect, onClear }) => {
  const [open, setOpen] = React.useState(false);
  const active = value && value.phrase;
  return (
    <div
      data-testid={testId}
      style={{
        marginTop: 12, marginBottom: 4,
        background: OL.surface2, border: `1px solid ${OL.border}`,
        borderRadius: 12, overflow: 'hidden',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px',
      }}>
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: OL.textMuted, flex: '0 0 auto',
        }}>Dignity</span>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`${testId}-toggle`}
          style={{
            flex: 1, textAlign: 'left', padding: '6px 10px',
            border: `1px solid ${active ? OL.clay : OL.border}`,
            background: active ? 'rgba(196,90,59,0.12)' : 'transparent',
            color: active ? OL.cream : OL.textDim,
            borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {active ? value.phrase : 'Suggest a dignity statement…'}
          </span>
          <span style={{ fontSize: 10, opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
        </button>
        {active && (
          <button
            onClick={onClear}
            data-testid={`${testId}-clear`}
            title="Clear suggestion"
            style={{
              border: 0, background: 'transparent', color: OL.textDim,
              fontSize: 12, cursor: 'pointer', padding: '4px 6px',
            }}
          >✕</button>
        )}
      </div>
      {open && (
        <div
          data-testid={`${testId}-menu`}
          style={{
            maxHeight: 220, overflowY: 'auto',
            borderTop: `1px solid ${OL.border}`, background: OL.surface,
          }}
        >
          {DIGNITY_STATEMENTS.map((group) => (
            <div key={group.category} style={{ padding: '8px 12px 6px' }}>
              <div style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: OL.textMuted, marginBottom: 4,
              }}>{group.category}</div>
              {group.phrases.map((phrase) => {
                const isPicked = active && value.phrase === phrase;
                return (
                  <button
                    key={phrase}
                    onClick={() => {
                      onSelect({ category: group.category, phrase });
                      setOpen(false);
                    }}
                    data-testid={`${testId}-opt`}
                    style={{
                      width: '100%', textAlign: 'left', padding: '6px 8px',
                      border: 0, borderRadius: 8,
                      background: isPicked ? 'rgba(196,90,59,0.16)' : 'transparent',
                      color: isPicked ? OL.cream : OL.textDim,
                      fontSize: 12, lineHeight: 1.4, cursor: 'pointer', marginBottom: 2,
                    }}
                  >{phrase}</button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Guide-side popup: pops in when the Coach picks a dignity suggestion.
// Guide can dismiss (only visual — clearing is done on Coach's side).
const DignitySuggestionPopup = ({ suggestion, testId }) => {
  const [dismissedFor, setDismissedFor] = React.useState(null);
  if (!suggestion || !suggestion.phrase) return null;
  if (dismissedFor === suggestion.phrase) return null;
  return (
    <div
      data-testid={testId}
      role="status"
      style={{
        position: 'absolute', top: 8, left: 8, right: 8, zIndex: 5,
        background: 'linear-gradient(180deg, #2A2118 0%, #241C13 100%)',
        border: `1px solid ${OL.clay}`, borderRadius: 12,
        padding: '10px 12px',
        boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4,
      }}>
        <span style={{
          fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: OL.clay, fontWeight: 700,
        }}>Coach's suggestion · {suggestion.category}</span>
        <button
          onClick={() => setDismissedFor(suggestion.phrase)}
          data-testid={`${testId}-dismiss`}
          style={{
            border: 0, background: 'transparent', color: OL.textDim,
            fontSize: 12, cursor: 'pointer', padding: 0, lineHeight: 1,
          }}
        >✕</button>
      </div>
      <div style={{
        fontFamily: '"DM Serif Display", Georgia, serif',
        fontSize: 16, lineHeight: 1.3, color: OL.cream,
      }}>{suggestion.phrase}</div>
      <div style={{ marginTop: 6, fontSize: 10, color: OL.textMuted, fontStyle: 'italic' }}>
        Suggestion only — say it your way.
      </div>
    </div>
  );
};

// Guide's Empathy Ladder ON/OFF toggle — sits in the top-right of the
// Guide panel header. When OFF, the ladder collapses to a paraphrase-focus
// meter view; when ON (default) the seven rungs are visible.
const LadderToggle = ({ state, write, testId }) => {
  const on = (state?.guideView || 'ladder') !== 'meter';
  return (
    <button
      onClick={() => write && write({ guideView: on ? 'meter' : 'ladder' })}
      data-testid={testId}
      data-on={on ? 'true' : 'false'}
      title={on ? 'Hide the Empathy Ladder' : 'Show the Empathy Ladder'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 8px 4px 10px', borderRadius: 999,
        border: `1px solid ${on ? OL.clay : OL.border2}`,
        background: on ? 'rgba(196,90,59,0.12)' : 'transparent',
        color: on ? OL.clay : OL.textDim,
        fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
        cursor: 'pointer', transition: 'all .15s ease',
      }}
    >
      Empathy Ladder
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 26, height: 16, borderRadius: 999,
        background: on ? OL.clay : OL.surface2,
        color: on ? '#fff' : OL.textDim,
        border: `1px solid ${on ? OL.clay : OL.border2}`,
        fontSize: 8, fontWeight: 700,
      }}>{on ? 'ON' : 'OFF'}</span>
    </button>
  );
};

// Small chip on the Guide's panel header — lights up the moment the
// Conflict player taps Knock knock or Ring ring. Purely presentational —
// the underlying state is `conflictEngaged`, written from the Conflict
// panel. Guide doesn't clear it; Conflict does when they re-tap.
const IncomingEngagementChip = ({ state, testId }) => {
  const engaged = state?.conflictEngaged;
  if (engaged !== 'knock' && engaged !== 'ring') return null;
  const label = engaged === 'ring' ? 'Ring ring' : 'Knock knock';
  return (
    <div
      data-testid={testId}
      data-engaged={engaged}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'rgba(196,90,59,0.18)',
        border: `1px solid ${OL.clay}`,
        color: OL.clay, fontWeight: 700,
        fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
        padding: '4px 8px', borderRadius: 999,
        animation: 'olPulseChip 1.2s ease-in-out infinite',
      }}
      title="Conflict is engaging — pick up the call"
    >
      <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>
        {engaged === 'ring' ? '☎' : '✊'}
      </span>
      {label}
    </div>
  );
};

export const GuidePanel = ({ state, write, testPrefix = 'ol', voiceCapture = null }) => {
  const count = countRungsHeard(state);
  const activeNudges = countActiveNudges(state);
  const maxNudges = nudgeMax(state);
  const ready = count >= 7;
  const nudgedRungs = (state.nudged || [])
    .map((n, i) => (n ? i : -1))
    .filter((i) => i >= 0);
  const ladderView = (state.guideView || 'ladder') !== 'meter';
  const stage = stageFromState(state);
  const stageDone = isStageDone(state, stage);
  const { voiceTimeDone, paraphrased, closingDone } = stageFlags(state);
  const allDone = !!state.paraphraseSubmitted && !!state.respectComplete && !!state.nextStepsComplete;
  const showBanner = !allDone && stage !== null;
  return (
    <Panel testId={`${testPrefix}-guide`} title="Guide" right={
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {voiceCapture}
        <LadderToggle state={state} write={write} testId={`${testPrefix}-guide-ladder-toggle`} />
        <IncomingEngagementChip state={state} testId={`${testPrefix}-guide-incoming`} />
        <NudgeDots used={activeNudges} max={maxNudges} />
      </div>
    }>
      {/* Coach's dignity suggestion — floats in when Coach picks; dismissable.
          Auto-hides once Respect is marked complete so the Guide isn't still
          looking at a stale prompt. */}
      {!state?.respectComplete && (
        <DignitySuggestionPopup
          suggestion={state?.dignitySuggestion}
          testId={`${testPrefix}-guide-dignity-popup`}
        />
      )}

      {/* Instruction mirror — same trigger + copy as Coach; read-only (no mark button). */}
      {showBanner && <ParaphrasePrompt testId={`${testPrefix}-guide-cta`} stage={stage} ready={ready} done={stageDone} />}

      {/* Voice + Time (read-only) — collapses once both are marked */}
      {!voiceTimeDone && (
        <div data-testid={`${testPrefix}-guide-voicetime`}>
          <SectionLabel>Coach&apos;s Signals</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <ListenIconBtn testId={`${testPrefix}-guide-voice`} icon={<IconVoice />} label="Voice"
              active={!!state.coachVoice} readOnly />
            <ListenIconBtn testId={`${testPrefix}-guide-time`} icon={<IconTime />} label="Time"
              active={!!state.coachTime} readOnly />
          </div>
        </div>
      )}

      {/* Story · Challenge micro-toggles — read-only mirror of Coach's picks.
          Positioned beneath Voice + Time. Blurred until Voice + Time complete.
          Collapses off-screen once Paraphrase is marked complete. */}
      {!paraphrased && (
        <BlurGate unlocked={voiceTimeDone} testId={`${testPrefix}-guide-toggles-gate`}>
          <MicroToggleRow state={state} readOnly testPrefix={`${testPrefix}-guide`} />
        </BlurGate>
      )}

      {/* Empathy Ladder (read-only) — blurred until Coach marks Time complete;
          collapses once Coach marks Paraphrase Complete */}
      {!paraphrased && (
        <BlurGate unlocked={voiceTimeDone} testId={`${testPrefix}-guide-ladder-gate`}>
          {!voiceTimeDone && <Divider />}
          <div
            data-testid={`${testPrefix}-guide-ladder-section`}
            data-ladder-locked={!state.coachTime ? 'true' : 'false'}
            style={{ position: 'relative' }}
          >
            <div style={{
              filter: state.coachTime ? 'none' : 'blur(6px)',
              pointerEvents: state.coachTime ? 'auto' : 'none',
              transition: 'filter .35s ease',
              userSelect: state.coachTime ? 'auto' : 'none',
            }}>
            <SectionLabel>{ladderView ? 'Empathy Ladder' : 'Paraphrase Focus'}</SectionLabel>

            {ladderView ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {RUNGS.map((r, i) => {
                  const isBeginner = (state.gameMode || 'beginner') === 'beginner';
                  const rungAns = (state.rungAnswers || {})[String(i)];
                  return (
                    <GuideRungChip
                      key={r.num}
                      testId={`${testPrefix}-guide-rung-${i}`}
                      num={r.num}
                      name={r.name}
                      tapped={!!(state.rungs || [])[i]}
                      nudged={!!(state.nudged || [])[i]}
                      beginnerOptions={isBeginner ? RUNG_OPTIONS[i] : undefined}
                      beginnerValue={rungAns}
                    />
                  );
                })}
              </div>
            ) : (
              <div style={{
                background: OL.surface2, border: `1px solid ${OL.border}`,
                borderRadius: 12, padding: '18px 16px', textAlign: 'center',
              }} data-testid={`${testPrefix}-guide-meter-only`}>
                <div style={{ fontSize: 11, color: OL.textDim, marginBottom: 10 }}>
                  {ready
                    ? 'You have every rung. Time to paraphrase back what you heard.'
                    : 'Focus on the paraphrase — Coach is tracking the rungs for you.'}
                </div>
                <ParaphraseMeter count={count} readOnly />
                <NudgeStrip nudged={nudgedRungs} testPrefix={testPrefix} />
              </div>
            )}
            </div>
          </div>

          {ladderView && state.coachTime && <><Divider /><ParaphraseMeter count={count} readOnly /></>}
        </BlurGate>
      )}

      {/* Respect + Next Steps (read-only mirror) — rendered from the start,
          blurred until Paraphrase is marked complete. Respect disappears
          once marked, leaving only Next Steps + its sub-toggles. */}
      {!closingDone && (
        <BlurGate unlocked={paraphrased} testId={`${testPrefix}-guide-closing-gate`}>
          <div data-testid={`${testPrefix}-guide-closing-section`} style={{ marginTop: paraphrased ? 0 : 4 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {!state.respectComplete && (
                <ActionIconBtn testId={`${testPrefix}-guide-handshake`} icon={<IconHandshake />} label="Respect"
                  active={!!state.coachHandshake} readOnly />
              )}
              <ActionIconBtn testId={`${testPrefix}-guide-scale`} icon={<IconScale />} label="Next Steps"
                active={!!state.coachScale} readOnly />
            </div>
            {!!state.coachScale && !state.nextStepsComplete && (
              <NextStepsSubToggles state={state} readOnly testPrefix={`${testPrefix}-guide`} />
            )}
          </div>
        </BlurGate>
      )}

      {/* End Game — read-only view for Guide */}
      {allDone && (
        <EndGamePanel testId={`${testPrefix}-guide-end-game`} canReset={false} />
      )}
    </Panel>
  );
};

// Nudged-rungs strip — small clay chips showing which rungs Coach nudged
const NudgeStrip = ({ nudged, testPrefix }) => {
  if (!nudged || nudged.length === 0) return null;
  return (
    <div
      data-testid={`${testPrefix}-guide-nudge-strip`}
      style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${OL.border2}` }}
    >
      <div style={{
        fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase',
        color: OL.clay, marginBottom: 6,
      }}>Coach nudged</div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
        {nudged.map((i) => {
          const r = RUNGS[i];
          return (
            <div
              key={i}
              title={r?.name}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: OL.clayGlow, border: `1px solid ${OL.clay}`,
                borderRadius: 999, padding: '4px 10px 4px 4px',
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: '50%', background: OL.clay,
                color: '#fff', fontSize: 10, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{r?.num}</div>
              <div style={{ fontSize: 11, color: OL.cream, fontWeight: 500 }}>{r?.name}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Long log-paraphrase button under the meter
const LogParaphraseButton = ({ ready, submitted, onClick, testId, stage = 'paraphrase' }) => {
  const copy = STAGE_COPY[stage] || STAGE_COPY.paraphrase;
  return (
  <button
    onClick={onClick}
    disabled={!ready}
    data-testid={testId}
    data-stage={copy.testStage}
    style={{
      width: '100%', padding: '14px 16px', borderRadius: 12,
      border: `1.5px solid ${submitted ? OL.forestLit : ready ? OL.clay : OL.border2}`,
      background: submitted ? OL.forest : ready ? OL.clay : OL.surface2,
      color: submitted ? OL.cream : ready ? '#fff' : OL.textMuted,
      cursor: ready ? 'pointer' : 'not-allowed',
      transition: 'all .22s ease',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      fontSize: 13, fontWeight: 600, letterSpacing: '0.02em',
    }}
  >
    {submitted ? (
      <>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        {copy.submittedBtn}
      </>
    ) : ready ? (
      <>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 20l4-1 12-12a2.83 2.83 0 0 0-4-4L4 15v5z" />
          <path d="M12.5 6.5l4 4" />
        </svg>
        {copy.btn}
      </>
    ) : (
      <>
        <span style={{ opacity: 0.7 }}>{copy.btn}</span>
        <span style={{ fontSize: 10, opacity: 0.55 }}>· unlocks at 7/7</span>
      </>
    )}
  </button>
  );
};

// Full paraphrase prompt banner — big, serif, pulsing
const STAGE_COPY = {
  paraphrase: {
    title: 'Paraphrase now.',
    sub: 'Reflect it back in your own words.',
    markLabel: 'Mark Paraphrase Complete',
    lockedSub: 'Unlocks when all 7 rungs are heard.',
    badge: '7',
    testStage: 'paraphrase',
    requiresReady: true,
  },
  dignity: {
    title: 'Make a Dignity Statement.',
    sub: 'See the person — not just the problem. Normalize · reflect understanding · remove shame.',
    markLabel: 'Mark Dignity Statement Complete',
    badge: 'R',
    testStage: 'dignity',
    requiresReady: false,
  },
  next: {
    title: 'Action · Choice · Consequence.',
    sub: 'Name what you\'ll do, what happens if you don\'t, or the clarity that there\'s no move here.',
    markLabel: 'Mark Next Steps Complete',
    badge: 'NS',
    testStage: 'next',
    requiresReady: false,
  },
};

// Phase is driven by which Coach TOGGLE is currently on (Respect / Next Steps),
// or by the meter reaching 7/7 for the paraphrase phase. Marking a task complete
// does NOT advance the phase — the phase only changes when Coach toggles again.
const stageFromState = (state) => {
  if (state?.coachScale) return 'next';
  if (state?.coachHandshake) return 'dignity';
  const count = countRungsHeard(state);
  if (count >= 7) return 'paraphrase';
  return null;
};

// Whether the task for the currently-shown stage has been marked complete.
const isStageDone = (state, stage) => {
  if (stage === 'paraphrase') return !!state?.paraphraseSubmitted;
  if (stage === 'dignity') return !!state?.respectComplete;
  if (stage === 'next') return !!state?.nextStepsComplete;
  return false;
};

const ParaphrasePrompt = ({ testId, stage = 'paraphrase', ready = true, done = false, onMarkComplete }) => {
  const copy = STAGE_COPY[stage] || STAGE_COPY.paraphrase;
  const locked = copy.requiresReady && !ready;
  const canClick = !locked && !done && !!onMarkComplete;
  return (
  <div
    data-testid={testId}
    data-stage={copy.testStage}
    data-done={done ? 'true' : 'false'}
    style={{
      position: 'relative',
      background: done
        ? `linear-gradient(135deg, ${OL.forest}66 0%, ${OL.forestLit}33 100%)`
        : `linear-gradient(135deg, ${OL.clayGlow} 0%, ${OL.forest}44 100%)`,
      border: `1.5px solid ${done ? OL.forestLit : OL.clay}`,
      borderRadius: 14,
      padding: '16px 18px',
      overflow: 'hidden',
      animation: 'ol-paraphrase-in .35s ease-out',
    }}
  >
    <style>{`
      @keyframes ol-paraphrase-in {
        from { opacity: 0; transform: translateY(-6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes ol-paraphrase-pulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(196, 90, 59, 0.35); }
        50%      { box-shadow: 0 0 0 10px rgba(196, 90, 59, 0); }
      }
      .ol-paraphrase-badge { animation: ol-paraphrase-pulse 2.2s ease-out infinite; }
    `}</style>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
      <div
        className={done ? '' : 'ol-paraphrase-badge'}
        style={{
          width: 34, height: 34, borderRadius: '50%',
          background: done ? OL.forestLit : OL.clay,
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, fontWeight: 700, flexShrink: 0,
        }}
      >{done ? '✓' : copy.badge}</div>
      <div style={{
        fontFamily: '"DM Serif Display", Georgia, serif',
        fontSize: 26, lineHeight: 1.05, color: OL.cream,
        letterSpacing: '-0.01em',
      }}>
        {copy.title}
      </div>
    </div>
    <div style={{ fontSize: 12, color: OL.textDim, lineHeight: 1.5, marginBottom: onMarkComplete ? 12 : 0 }}>
      {locked ? copy.lockedSub : copy.sub}
    </div>
    {onMarkComplete && (
      <button
        onClick={canClick ? onMarkComplete : undefined}
        disabled={!canClick}
        data-testid={`${testId}-mark`}
        style={{
          width: '100%',
          padding: '12px 14px', borderRadius: 12, border: 0,
          background: done ? OL.forest : canClick ? OL.clay : OL.surface2,
          color: done ? OL.cream : canClick ? '#fff' : OL.textMuted,
          fontSize: 13, fontWeight: 600, letterSpacing: '0.02em',
          cursor: canClick ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        {done ? 'Task marked complete' : copy.markLabel}
      </button>
    )}
  </div>
  );
};

// Conflict Focus (Guide / Other / Self) — CC sets the direction of the
// conflict before the round starts. Locks the moment the Coach marks
// either Voice or Time (game-start signal). Read-only on the Conflict panel.
const CONFLICT_FOCUS_OPTIONS = [
  { value: 'guide', label: 'Guide' },
  { value: 'other', label: 'Other' },
  { value: 'self', label: 'Self' },
];

const ConflictFocusRow = ({ state, write, readOnly, testPrefix }) => {
  const value = state?.conflictFocus === 'other' ? 'other'
              : state?.conflictFocus === 'self' ? 'self'
              : 'guide';
  const gameStarted = !!(state?.coachVoice || state?.coachTime);
  const locked = readOnly || gameStarted;
  return (
    <div
      data-testid={`${testPrefix}-focus`}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginBottom: 8,
      }}
    >
      <span style={{
        fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: OL.textMuted, fontWeight: 600, minWidth: 44,
      }}>Focus{locked && !readOnly ? ' · locked' : ''}</span>
      <div style={{
        display: 'inline-flex', gap: 2, background: OL.surface2,
        border: `1px solid ${OL.border2}`, borderRadius: 999, padding: 2,
        opacity: locked && !readOnly ? 0.6 : 1,
      }}>
        {CONFLICT_FOCUS_OPTIONS.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={locked ? undefined : () => write && write({ conflictFocus: opt.value })}
              disabled={locked}
              data-testid={`${testPrefix}-focus-opt-${opt.value}`}
              data-active={active ? 'true' : 'false'}
              style={{
                minWidth: 46, height: 22, borderRadius: 999, border: 0,
                padding: '0 10px',
                background: active ? OL.clay : 'transparent',
                color: active ? '#fff' : OL.textDim,
                fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                cursor: locked ? 'default' : 'pointer',
              }}
            >{opt.label}</button>
          );
        })}
      </div>
    </div>
  );
};

// Prompt on the Conflict panel: when ready, tap Knock knock or Ring ring
// to engage the Guide. The choice is persisted on the room state so the
// Guide sees an "incoming" chip immediately. Tapping the active signal
// again clears it (in case the Conflict wants to reset before entering).
const EngageGuidePrompt = ({ state, write, testPrefix }) => {
  const engaged = state?.conflictEngaged === 'knock' || state?.conflictEngaged === 'ring'
    ? state.conflictEngaged
    : null;
  const pick = (val) => {
    if (!write) return;
    write({ conflictEngaged: engaged === val ? null : val });
  };
  return (
    <div
      data-testid={`${testPrefix}-engage`}
      style={{
        background: engaged ? 'rgba(196,90,59,0.15)' : OL.surface2,
        border: `1px solid ${engaged ? OL.clay : OL.border}`,
        borderRadius: 12, padding: '10px 12px', marginBottom: 10,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{
        fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: engaged ? OL.clay : OL.textMuted, fontWeight: 700,
      }}>
        {engaged ? 'Engaged — Guide is up' : 'When ready, engage the Guide'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {[
          { value: 'knock', label: 'Knock knock' },
          { value: 'ring', label: 'Ring ring' },
        ].map((opt) => {
          const active = engaged === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => pick(opt.value)}
              data-testid={`${testPrefix}-engage-${opt.value}`}
              data-active={active ? 'true' : 'false'}
              style={{
                padding: '9px 10px', borderRadius: 999,
                border: `1px solid ${active ? OL.clay : OL.border}`,
                background: active ? OL.clay : 'transparent',
                color: active ? '#fff' : OL.cream,
                fontSize: 12.5, fontWeight: 600, letterSpacing: '0.02em',
                cursor: 'pointer', transition: 'background 120ms, border 120ms',
              }}
            >{opt.label}</button>
          );
        })}
      </div>
    </div>
  );
};

export const ConflictPanel = ({ state, write, testPrefix = 'ol', voiceCapture = null }) => {
  const drawn = state.drawnRung || 0;
  // Auto-assign a random scenario (0 or 1) whenever one hasn't been picked yet.
  React.useEffect(() => {
    if (state.selectedScenario == null && typeof write === 'function') {
      write({ selectedScenario: Math.floor(Math.random() * 2) });
    }
  }, [state.selectedScenario, drawn, write]);
  const redraw = () => {
    let next = Math.floor(Math.random() * 7);
    // 80% chance avoid repeat
    if (next === drawn && Math.random() < 0.8) next = (drawn + 1 + Math.floor(Math.random() * 6)) % 7;
    // Assign the new scenario at random too — no user pick.
    write({ drawnRung: next, selectedScenario: Math.floor(Math.random() * 2) });
  };
  return (
    <Panel testId={`${testPrefix}-conflict`} title="Conflict" right={
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {voiceCapture}
        <span style={{ fontSize: 10, color: OL.textDim }}>role</span>
      </div>
    }>
      {/* Focus mirror — read-only. CC picks; Conflict sees the direction. */}
      <ConflictFocusRow state={state} readOnly testPrefix={`${testPrefix}-conflict`} />
      <EngageGuidePrompt state={state} write={write} testPrefix={`${testPrefix}-conflict`} />
      <ThreatMeter level={state.threat || 1} />
      <Divider />
      <ConflictCard
        drawnRung={drawn}
        selectedScenario={state.selectedScenario == null ? null : state.selectedScenario}
        onRedraw={redraw}
      />
    </Panel>
  );
};

export const ConflictCoachPanel = ({ state, write, testPrefix = 'ol', voiceCapture = null }) => (
  <Panel testId={`${testPrefix}-cc`} title="Conflict Coach" right={
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {voiceCapture}
      <span style={{ fontSize: 10, color: OL.textDim }}>role</span>
    </div>
  }>
    {/* Focus — CC steers direction. Locks once Coach starts the round. */}
    <ConflictFocusRow state={state} write={write} testPrefix={`${testPrefix}-cc`} />
    <ThreatMeter level={state.threat || 1} editable onChange={(n) => write({ threat: n })} />
    <Divider />
    <div style={{
      background: OL.surface2, border: `1px solid ${OL.border}`,
      borderRadius: 12, padding: '12px 14px', color: OL.textDim, fontSize: 12.5,
      lineHeight: 1.5,
    }}>
      Shadow the Conflict player. Escalate when disruptions aren't landing,
      de-escalate when the Guide gets a technique through. Keep it honest —
      the goal is to let the Guide land moves, not to win.
    </div>
  </Panel>
);
